/**
 * CameraGalleryPopup
 *
 * Gallery-style popup shown when clicking a non-expandable camera cluster
 * (cameras at the same or nearly identical coordinates). Displays a grid
 * of camera cards with live thumbnails, status indicators, and incident badges.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Button,
  Skeleton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import SendIcon from '@mui/icons-material/Send';
import { CameraLocation } from './types';
import styles from './CameraGalleryPopup.module.css';

interface CameraGalleryPopupProps {
  /** Cameras to display in the gallery */
  cameras: CameraLocation[];
  /** Callback when popup is closed */
  onClose: () => void;
  /** Callback when a camera card is clicked to view its stream */
  onViewStream: (camera: CameraLocation) => void;
  /** Callback when "View Alerts" is clicked on a camera */
  onViewAlerts?: (camera: CameraLocation) => void;
  /** Callback when "Dispatch" is clicked. Undefined = disabled (no customer/address linked). */
  onDispatch?: () => void;
}

/** Snapshot refresh interval in milliseconds */
const SNAPSHOT_REFRESH_INTERVAL = 5000;

/** Status color mapping */
const STATUS_COLORS: Record<string, string> = {
  online: '#4CAF50',
  offline: '#9E9E9E',
  unknown: '#FFC107',
};

/**
 * Build the snapshot URL for a camera (same pattern as CameraPopup).
 */
const getSnapshotUrl = (siteId: number, cameraId: number, timestamp: number): string => {
  const baseURL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:10000'
    : '';
  return `${baseURL}/api3.2/canvms/sites/${siteId}/cameras/${cameraId}/proxy/snapshot?t=${timestamp}`;
};

/**
 * Individual camera card within the gallery grid.
 */
const CameraCard: React.FC<{
  camera: CameraLocation;
  timestamp: number;
  onViewStream: (camera: CameraLocation) => void;
  onViewAlerts?: (camera: CameraLocation) => void;
}> = ({ camera, timestamp, onViewStream, onViewAlerts }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const isOnline = camera.status === 'online';
  const hasAlerts = camera.alert_count > 0;
  const displayName = camera.display_name || camera.name;
  const statusColor = STATUS_COLORS[camera.status] ?? '#9E9E9E';

  // Reset image state when camera or timestamp changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [camera.id, timestamp]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setImageLoaded(true);
    setImageError(true);
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewStream(camera);
  };

  const handleAlertClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewAlerts?.(camera);
  };

  return (
    <div className={styles.card} onClick={handleClick} title={displayName}>
      {/* Thumbnail */}
      <div className={styles.cardThumbnail}>
        {isOnline && !imageLoaded && !imageError && (
          <Skeleton
            variant="rectangular"
            className={styles.cardSkeleton}
            animation="wave"
          />
        )}

        {isOnline && !imageError && (
          <img
            src={getSnapshotUrl(camera.site_id, camera.id, timestamp)}
            alt={`${displayName} snapshot`}
            className={styles.cardImage}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ display: imageLoaded ? 'block' : 'none' }}
          />
        )}

        {(imageError || !isOnline) && (
          <div className={styles.cardPlaceholder}>
            <VideocamOffIcon className={styles.cardPlaceholderIcon} />
            <Typography variant="caption" color="textSecondary" style={{ fontSize: 10 }}>
              {!isOnline ? 'Offline' : 'No preview'}
            </Typography>
          </div>
        )}

        {/* Incident badge */}
        {hasAlerts && (
          <div className={styles.cardAlertBadge}>
            {camera.alert_count > 9 ? '9+' : camera.alert_count}
          </div>
        )}

        {/* Name + status overlay on thumbnail */}
        <div className={styles.cardOverlay}>
          <div className={styles.cardOverlayRow}>
            <div
              className={styles.cardStatusDot}
              style={{ backgroundColor: statusColor }}
            />
            <span className={styles.cardOverlayName}>{displayName}</span>
          </div>
          {hasAlerts && (
            <span
              className={styles.cardOverlayAlerts}
              onClick={handleAlertClick}
            >
              {camera.alert_count} alert{camera.alert_count !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Gallery popup showing all cameras from a non-expandable cluster.
 */
const CameraGalleryPopup: React.FC<CameraGalleryPopupProps> = ({
  cameras,
  onClose,
  onViewStream,
  onViewAlerts,
  onDispatch,
}) => {
  const [timestamp, setTimestamp] = useState(Date.now());

  // Refresh thumbnails every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTimestamp(Date.now());
    }, SNAPSHOT_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  // Derive site name from cameras (they're co-located, so use first)
  const siteName = cameras[0]?.site_name ?? 'Unknown Site';
  const cameraCount = cameras.length;

  return (
    <Paper className={styles.popup} elevation={4}>
      {/* Header */}
      <Box className={styles.header}>
        <Box className={styles.headerContent}>
          <Typography className={styles.title}>
            {cameraCount} Camera{cameraCount !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="body2" color="textSecondary" className={styles.subtitle}>
            {siteName}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          className={styles.closeButton}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Camera grid */}
      <div className={styles.grid}>
        {cameras.map((camera) => (
          <CameraCard
            key={camera.id}
            camera={camera}
            timestamp={timestamp}
            onViewStream={onViewStream}
            onViewAlerts={onViewAlerts}
          />
        ))}
      </div>

      {/* Dispatch action */}
      <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
        <Tooltip
          title={onDispatch ? 'Open scheduler to create a patrol at this site' : 'Cameras have no linked customer/address'}
          arrow
        >
          <span>
            <Button
              variant="contained"
              size="small"
              color="primary"
              startIcon={<SendIcon />}
              onClick={(e) => { e.stopPropagation(); onDispatch?.(); }}
              disabled={!onDispatch}
              fullWidth
            >
              Dispatch
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default CameraGalleryPopup;
